import React, { useContext, useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faFileInvoice,
  faCalendarAlt,
  faTrafficLight,
  faCalendar,
  faFilter,
  faTimes,
  faBuilding,
  faIdCard,
  faCog,
  faMapMarkerAlt,
  faClipboardCheck,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import MiniDashboardTraffic from "./MiniDashboardTraffic";
import logo from "../buckland.png";
import Pagination from "../utils/Pagination";
import ExportCSVButton from "../utils/ExportCSVButton";
import SearchForm from "../utils/SearchForm";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useAuth } from "../contexts/AuthContext";
import { getTranslation } from "../utils/i18n";

Modal.setAppElement("#root");

const TrafficComponent = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } =
    useContext(DataContext); // Added setShouldFetchData
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const [expandedRows, setExpandedRows] = useState([]);
  const [modalData, setModalData] = useState(null);

  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedRfcs, setSelectedRfcs] = useState([]);
  const [rfcOptions, setRfcOptions] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [operationTypeOptions, setOperationTypeOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedPedimentos, setSelectedPedimentos] = useState([]);
  const [pedimentoOptions, setPedimentoOptions] = useState([]);
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Open", label: "Open" },
  ];

  const [sortConfig, setSortConfig] = useState({
    key: "detail.dates_data.date_init",
    direction: "desc",
  });

  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [language, setLanguage] = useState("en"); // State to manage the current language
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [selectKey, setSelectKey] = useState(0); //For search cleaning

  // New useEffect for setShouldFetchData
  useEffect(() => {
    // Set the flag to true when the component mounts
    setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: true }));

    // Clean up the flag when the component unmounts
    return () => {
      setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: false }));
    };
  }, [setShouldFetchData]);

  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  useEffect(() => {
    let filtered = trafficData || [];
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        // Function to check if any value in an object or its nested objects includes the search term
        const checkObjectForMatch = (obj, term) => {
          if (!obj) return false;

          for (const key in obj) {
            const value = obj[key];

            // If the value is an object, recursively check it <-- Thanks Claude!
            if (value && typeof value === "object" && !Array.isArray(value)) {
              if (checkObjectForMatch(value, term)) {
                return true;
              }
            }
            // Otherwise check if the value includes the search term
            else if (String(value).toLowerCase().includes(term)) {
              return true;
            }
          }

          return false;
        };

        return checkObjectForMatch(item.header, searchTerm);
      });
    }
    if (selectedCustomers.length) {
      filtered = filtered.filter((item) =>
        selectedCustomers.some(
          (selected) => selected.value === item.header.customer_name
        )
      );
    }
    if (selectedDivisions.length) {
      filtered = filtered.filter((item) =>
        selectedDivisions.some(
          (selected) => selected.value === item.header.customer_division
        )
      );
    }
    if (selectedRfcs.length) {
      filtered = filtered.filter((item) =>
        selectedRfcs.some(
          (selected) => selected.value === item.header.customer_rfc
        )
      );
    }
    if (selectedOperationTypes.length) {
      filtered = filtered.filter((item) =>
        selectedOperationTypes.some(
          (selected) => selected.value === item.header.operation_type
        )
      );
    }
    if (selectedEntryPoints.length) {
      filtered = filtered.filter((item) =>
        selectedEntryPoints.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_entry_point
        )
      );
    }
    if (selectedPedimentos.length) {
      filtered = filtered.filter((item) =>
        selectedPedimentos.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_number
        )
      );
    }
    if (selectedStatus.length) {
      filtered = filtered.filter((item) => {
        const status =
          item.header.detail?.traffic_data?.status === "Pagado"
            ? "Paid"
            : "Open";
        return selectedStatus.some((selected) => selected.value === status);
      });
    }
    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dateInit = item.header.detail?.dates_data?.date_init
          ? new Date(item.header.detail.dates_data.date_init)
          : null;
        return dateInit && dateInit >= startDate && dateInit <= endDate;
      });
    }
    setFilteredData(filtered);
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedDivisions,
    selectedRfcs,
    selectedOperationTypes,
    selectedEntryPoints,
    selectedPedimentos,
    selectedStatus,
    dateRange,
  ]);

  useEffect(() => {
    if (trafficData) {
      const uniqueDivisions = Array.from(
        new Set(trafficData.map((item) => item.header.customer_division))
      ).map((division) => ({ value: division, label: division }));
      setDivisionOptions(uniqueDivisions);
    }
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      const uniqueRfcs = Array.from(
        new Set(trafficData.map((item) => item.header.customer_rfc))
      ).map((rfc) => ({ value: rfc, label: rfc }));
      setRfcOptions(uniqueRfcs);
    }
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      const uniquePedimentos = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento.pedimento_number)
            .filter(Boolean) // Move the filter to the array before creating the Set
        )
      ).map((pedimento) => ({ value: pedimento, label: pedimento }));
      setPedimentoOptions(uniquePedimentos);
    }
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      const uniqueCustomers = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      ).map((customer) => ({ value: customer, label: customer }));
      setCustomerOptions(uniqueCustomers);
    }
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      const uniqueOperationTypes = Array.from(
        new Set(trafficData.map((item) => item.header.operation_type))
      ).map((operationType) => ({
        value: operationType,
        label: getOperationTypeText(operationType),
      }));
      setOperationTypeOptions(uniqueOperationTypes);
    }
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      const uniqueEntryPoints = Array.from(
        new Set(
          trafficData.map((item) => item.header.pedimento.pedimento_entry_point)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));
      setEntryPointOptions(uniqueEntryPoints);
    }
  }, [trafficData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    selectedCustomers,
    selectedDivisions,
    selectedRfcs,
    selectedOperationTypes,
    selectedEntryPoints,
    selectedStatus,
    dateRange,
  ]);

  const calculateAttentionTimeValue = (datesData) => {
    const dateInit = new Date(datesData?.date_init);
    const dateCrossing = new Date(datesData?.date_crossing);
    if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
      return Number.MAX_VALUE; // Return a large number so that "N/A" values are sorted last
    }
    return (dateCrossing - dateInit) / (1000 * 60 * 60);
  };

  const calculateDocTimeValue = (datesData) => {
    const dateInit = new Date(datesData?.date_init);
    const dateDoda = new Date(datesData?.date_doda);
    if (
      isNaN(dateInit.getTime()) ||
      isNaN(dateDoda.getTime()) ||
      dateDoda <= dateInit
    ) {
      return Number.MAX_VALUE; // Return a large number so that "N/A" values are sorted last
    }
    return (dateDoda - dateInit) / (1000 * 60 * 60);
  };

  const clearAllFilters = () => {
    // Reset all state variables
    setSelectedCustomers([]);
    setSelectedDivisions([]);
    setSelectedRfcs([]);
    setSelectedOperationTypes([]);
    setSelectedEntryPoints([]);
    setSelectedPedimentos([]);
    setSelectedStatus([]);
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);

    // Force re-render of Select components
    setSelectKey((prevKey) => prevKey + 1);

    // Force re-render of SearchForm
    setSearchResetKey((prevKey) => prevKey + 1);
  };

  const clearSearchFilter = () => {
    setSearchTerm("");
    setSearchResetKey((prevKey) => prevKey + 1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term.toLowerCase());
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to page 1 when changing page size
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType; // Return the original value if it's not 1 or 2
  };

  const getTransportIcon = (entryPoint) => {
    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    return transportIconsMap[entryPoint] || transportIconsMap["default"];
  };

  const clearFilterType = (filterType) => {
    switch (filterType) {
      case "customers":
        setSelectedCustomers([]);
        break;
      case "divisions":
        setSelectedDivisions([]);
        break;
      case "rfcs":
        setSelectedRfcs([]);
        break;
      case "operationTypes":
        setSelectedOperationTypes([]);
        break;
      case "entryPoints":
        setSelectedEntryPoints([]);
        break;
      case "pedimentos":
        setSelectedPedimentos([]);
        break;
      case "status":
        setSelectedStatus([]);
        break;
      case "search":
        setSearchTerm("");
        break;
      case "dateRange":
        setDateRange([
          {
            startDate: null,
            endDate: null,
            key: "selection",
          },
        ]);
        break;
      default:
        break;
    }
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue, bValue;

      const getFieldValue = (item, keyPath) => {
        return (
          keyPath.split(".").reduce((acc, key) => acc && acc[key], item) || ""
        );
      };

      aValue = getFieldValue(a.header, sortConfig.key);
      bValue = getFieldValue(b.header, sortConfig.key);

      // Handle specific fields (dates, numbers)
      if (sortConfig.key === "created") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortConfig.key === "attention_time") {
        aValue = calculateAttentionTimeValue(a.header.detail?.dates_data);
        bValue = calculateAttentionTimeValue(b.header.detail?.dates_data);
      } else if (sortConfig.key === "doc_time") {
        aValue = calculateDocTimeValue(a.header.detail?.dates_data);
        bValue = calculateDocTimeValue(b.header.detail?.dates_data);
      } else {
        // Convert to numbers if possible for proper numeric sorting
        if (!isNaN(aValue) && !isNaN(bValue)) {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        } else {
          // Ensure consistent casing for strings
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [currentPage, pageSize, sortedData]);

  const calculateAttentionTime = (datesData) => {
    const dateInit = new Date(datesData?.date_init);
    const dateCrossing = new Date(datesData?.date_crossing);
    let timeValue;
    let backgroundColor = "bg-gray-500";

    if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
      timeValue = "N/A";
    } else {
      const differenceInHours = (dateCrossing - dateInit) / (1000 * 60 * 60);
      timeValue = differenceInHours.toFixed(2);

      if (timeValue > 0.1 && timeValue <= 300) {
        backgroundColor = "bg-emerald-500";
      } else if (timeValue > 300.1 && timeValue <= 380) {
        backgroundColor = "bg-amber-500";
      } else {
        backgroundColor = "bg-red-500";
      }
    }

    return (
      <span
        className={`inline-flex items-center justify-center w-16 ${backgroundColor} text-white font-medium px-2 py-1 rounded-md text-xs`}
      >
        {timeValue}
      </span>
    );
  };

  // Similar update for calculateDocTime function

  const calculateDocTime = (datesData) => {
    const dateInit = new Date(datesData?.date_init);
    const dateDoda = new Date(datesData?.date_doda);
    let timeValue;
    let backgroundColor = "bg-gray-500";

    if (
      isNaN(dateInit.getTime()) ||
      isNaN(dateDoda.getTime()) ||
      dateDoda <= dateInit
    ) {
      timeValue = "N/A";
    } else {
      const differenceInHours = (dateDoda - dateInit) / (1000 * 60 * 60);
      timeValue = differenceInHours.toFixed(2);

      if (timeValue > 0.1 && timeValue <= 20) {
        backgroundColor = "bg-green-500";
      } else if (timeValue > 20.1 && timeValue <= 40) {
        backgroundColor = "bg-yellow-500";
      } else {
        backgroundColor = "bg-red-500";
      }
    }

    return (
      <span
        className={`inline-block w-16 text-center ${backgroundColor} text-white font-bold px-2 py-1 rounded`}
      >
        {timeValue}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const options = { month: "long", day: "numeric", year: "numeric" };
    const date = new Date(dateString);
    return isNaN(date)
      ? "Date N/A"
      : date.toLocaleDateString(undefined, options);
  };

  const handleRowClick = (reference) => {
    const isRowCurrentlyExpanded = expandedRows.includes(reference);
    const newExpandedRows = isRowCurrentlyExpanded
      ? expandedRows.filter((id) => id !== reference)
      : [...expandedRows, reference];
    setExpandedRows(newExpandedRows);
  };

  const transformDataForCSV = (data) => {
    return data.map((item) => {
      // Extract basic header fields
      const basicFields = {
        reference: item.header.reference,
        trailer_number: item.header.trailer_number,
        purchase_order: item.header.purchase_order,
        total_packages: item.header.total_packages,
        weight: item.header.weight,
        delivery_type: item.header.delivery_type,
        operation_type: getOperationTypeText(item.header.operation_type),
        customer_name: item.header.customer_name,
        customer_id: item.header.customer_id,
        customer_rfc: item.header.customer_rfc,
        customer_division: item.header.customer_division,
      };

      // Extract pedimento fields
      const pedimentoFields = {
        pedimento_license: item.header.pedimento.pedimento_license,
        pedimento_entry_point: item.header.pedimento.pedimento_entry_point,
        pedimento_number: item.header.pedimento.pedimento_number,
        pedimento_consolidation: item.header.pedimento.pedimento_consolidation,
        pedimento_remesa: item.header.pedimento.pedimento_remesa,
      };

      // Extract date fields
      const datesData = item.header.detail?.dates_data || {};
      const dateFields = {
        date_init: datesData.date_init || "",
        date_inbound: datesData.date_inbound || "",
        date_end: datesData.date_end || "",
        date_init_loading: datesData.date_init_loading || "",
        date_init_unloading: datesData.date_init_unloading || "",
        date_loading_order: datesData.date_loading_order || "",
        date_yard_outbound: datesData.date_yard_outbound || "",
        date_doda: datesData.date_doda || "",
        date_crossing: datesData.date_crossing || "",
        date_pedimento_paid: datesData.date_pedimento_paid || "",
      };

      // Extract traffic data fields
      const trafficData = item.header.detail?.traffic_data || {};
      const trafficFields = {
        status: trafficData.status || "",
        clave_ped: trafficData.clave_ped || "",
        regime: trafficData.regime || "",
        total_taxes: trafficData.total_taxes || "",
        total_taxes_requested_date:
          trafficData.total_taxes_requested_date || "",
        mexican_carrier: trafficData.mexican_carrier || "",
        transport_code: trafficData.transport_code || "",
        mexican_carrier_trailer_number:
          trafficData.mexican_carrier_trailer_number || "",
        us_carrier: trafficData.us_carrier || "",
        us_carrier_trailer_number: trafficData.us_carrier_trailer_number || "",
        cargo: trafficData.cargo || "",
        observations: trafficData.observations || "",
        traffic_observations: trafficData.traffic_observations || "",
        annotations: trafficData.annotations || "",
      };

      // Extract first bill of lading information (if exists)
      const billOfLadingData =
        item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
          ? {
              bill_of_lading:
                item.header.bill_of_lading_data[0].bill_of_lading || "",
              bill_of_lading_urgent:
                item.header.bill_of_lading_data[0].bill_of_lading_urgent || "",
              date_created_bill_of_lading_urgent:
                item.header.bill_of_lading_data[0]
                  .date_created_bill_of_lading_urgent || "",
              date_arrival_bill_of_lading_urgent:
                item.header.bill_of_lading_data[0]
                  .date_arrival_bill_of_lading_urgent || "",
            }
          : {
              bill_of_lading: "",
              bill_of_lading_urgent: "",
              date_created_bill_of_lading_urgent: "",
              date_arrival_bill_of_lading_urgent: "",
            };

      // Combine all sections into one flat object for CSV export
      return {
        ...basicFields,
        ...pedimentoFields,
        ...dateFields,
        ...trafficFields,
        ...billOfLadingData,
      };
    });
  };

  const dateStatuses = [
    { key: "date_init", label: "Init", color: "yellow" },
    { key: "date_inbound", label: "Inbound", color: "orange" },
    { key: "date_end", label: "Complete", color: "dark green" },
  ];

  const getColorClass = (color) => {
    const colorMap = {
      yellow: "bg-yellow-500",
      orange: "bg-orange-500",
      "darker yellow": "bg-yellow-700",
      "darker orange": "bg-orange-700",
      purple: "bg-purple-500",
      blue: "bg-blue-500",
      "darker blue": "bg-blue-700",
      lime: "bg-lime-500",
      "dark green": "bg-green-700",
    };
    return colorMap[color] || "bg-gray-500";
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const TrafficDetailSection = ({ currentItemDetails }) => {
    const [activeTab, setActiveTab] = useState("main");

    if (!currentItemDetails) return null;

    const {
      reference,
      trailer_number,
      purchase_order,
      bill_of_lading_data,
      total_packages,
      weight,
      delivery_type,
      operation_type,
      customer_name,
      customer_id,
      customer_rfc,
      pedimento: {
        pedimento_license,
        pedimento_entry_point,
        pedimento_number,
        pedimento_consolidation,
        pedimento_remesa,
      },
      detail: { dates_data, traffic_data, invoice_data },
    } = currentItemDetails.header;

    const TabPanel = ({ children, active }) => {
      return (
        <div className={`${active ? "block" : "hidden"} w-full pt-4`}>
          {children}
        </div>
      );
    };

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Tab navigation */}
        <div className="bg-blue-50 border-b border-blue-100">
          <div className="flex overflow-x-auto">
            <button
              className={`px-4 py-3 text-sm font-medium focus:outline-none ${
                activeTab === "main"
                  ? "text-blue-700 border-b-2 border-blue-500 bg-white"
                  : "text-blue-500 hover:text-blue-700"
              }`}
              onClick={() => setActiveTab("main")}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              Main Information
            </button>
            {bill_of_lading_data && bill_of_lading_data.length > 0 && (
              <button
                className={`px-4 py-3 text-sm font-medium focus:outline-none ${
                  activeTab === "bill"
                    ? "text-blue-700 border-b-2 border-blue-500 bg-white"
                    : "text-blue-500 hover:text-blue-700"
                }`}
                onClick={() => setActiveTab("bill")}
              >
                <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                Bill of Lading
              </button>
            )}
            <button
              className={`px-4 py-3 text-sm font-medium focus:outline-none ${
                activeTab === "dates"
                  ? "text-blue-700 border-b-2 border-blue-500 bg-white"
                  : "text-blue-500 hover:text-blue-700"
              }`}
              onClick={() => setActiveTab("dates")}
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
              Dates
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium focus:outline-none ${
                activeTab === "traffic"
                  ? "text-blue-700 border-b-2 border-blue-500 bg-white"
                  : "text-blue-500 hover:text-blue-700"
              }`}
              onClick={() => setActiveTab("traffic")}
            >
              <FontAwesomeIcon icon={faTrafficLight} className="mr-2" />
              Traffic Data
            </button>
            {invoice_data && invoice_data.length > 0 && (
              <button
                className={`px-4 py-3 text-sm font-medium focus:outline-none ${
                  activeTab === "invoice"
                    ? "text-blue-700 border-b-2 border-blue-500 bg-white"
                    : "text-blue-500 hover:text-blue-700"
                }`}
                onClick={() => setActiveTab("invoice")}
              >
                <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                Invoice
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-4 bg-white">
          {/* Main Information Tab */}
          <TabPanel active={activeTab === "main"}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 pb-2 border-b border-blue-100 mb-2">
                  Reference Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Reference:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {reference}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Trailer Number:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {trailer_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Purchase Order:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {purchase_order}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Total Packages:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {total_packages}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Weight:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {weight}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 pb-2 border-b border-blue-100 mb-2">
                  Customer Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Customer Name:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {customer_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Customer ID:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {customer_id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Customer RFC:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {customer_rfc}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Delivery Type:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {delivery_type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Operation Type:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {operation_type}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 pb-2 border-b border-blue-100 mb-2">
                  Pedimento Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">License:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {pedimento_license}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-xs">Entry Point:</span>
                    <span className="text-gray-900 font-medium text-xs flex items-center">
                      <FontAwesomeIcon
                        icon={getTransportIcon(pedimento_entry_point).icon}
                        className={`${
                          getTransportIcon(pedimento_entry_point).color
                        } mr-1`}
                        title={getTransportIcon(pedimento_entry_point).title}
                      />
                      {pedimento_entry_point}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Number:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {pedimento_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">
                      Consolidation:
                    </span>
                    <span className="text-gray-900 font-medium text-xs">
                      {pedimento_consolidation}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-xs">Remesa:</span>
                    <span className="text-gray-900 font-medium text-xs">
                      {pedimento_remesa}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Bill of Lading Tab */}
          <TabPanel active={activeTab === "bill"}>
            {bill_of_lading_data && bill_of_lading_data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bill of Lading
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Urgent
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Arrival Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bill_of_lading_data.map((bill, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-4 py-2 text-xs text-gray-900">
                          {bill.bill_of_lading}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-900">
                          {bill.bill_of_lading_urgent}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-900">
                          {formatDate(bill.date_created_bill_of_lading_urgent)}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-900">
                          {formatDate(bill.date_arrival_bill_of_lading_urgent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabPanel>

          {/* Dates Tab */}
          <TabPanel active={activeTab === "dates"}>
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {dates_data &&
                  Object.keys(dates_data).map((key, index) => (
                    <div key={index} className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs font-medium text-blue-700 mb-1">
                        {capitalizeFirstLetter(key.replace(/_/g, " "))}
                      </div>
                      <div className="text-sm text-gray-800">
                        {formatDate(dates_data[key])}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </TabPanel>

          {/* Traffic Data Tab */}
          <TabPanel active={activeTab === "traffic"}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {traffic_data &&
                Object.keys(traffic_data).map((key, index) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs font-medium text-blue-700 mb-1">
                      {capitalizeFirstLetter(key.replace(/_/g, " "))}
                    </div>
                    <div className="text-sm text-gray-800">
                      {traffic_data[key]}
                    </div>
                  </div>
                ))}
            </div>
          </TabPanel>

          {/* Invoice Tab */}
          <TabPanel active={activeTab === "invoice"}>
            {invoice_data && invoice_data.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {invoice_data.map((invoice, index) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-800 pb-2 border-b border-blue-100 mb-2">
                      Invoice #{index + 1}
                    </h3>
                    <div className="space-y-1">
                      {Object.keys(invoice).map((key) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-600 text-xs">
                            {capitalizeFirstLetter(key.replace(/_/g, " "))}:
                          </span>
                          <span className="text-gray-900 font-medium text-xs">
                            {invoice[key]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabPanel>
        </div>
      </div>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDetailsClick = (item) => {
    // Add validation before storing data
    if (!item || !item.header) {
      console.error("Invalid item data for details view:", item);
      return;
    }

    // Store the data in sessionStorage
    sessionStorage.setItem("trafficDetailsItem", JSON.stringify(item));

    // Open in a new tab
    window.open(`/trafficDetails?ref=${item.header.reference}`, "_blank");
  };

  const openModal = (data) => {
    setModalData(data);
  };

  const closeModal = () => {
    setModalData(null);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    }
    return null;
  };

  const renderHeader = () => (
    <div
      className="w-full flex justify-between items-center px-2 md:px-4 py-2 md:py-4"
      style={{ backgroundColor: "#1A2654" }}
    >
      <div className="flex items-center">
        <img src={logo} alt="Company Logo" className="h-16 mr-2" />
      </div>
      <div className="ml-auto flex items-center">
        <h1 className="text-xl font-semibold text-white mr-4">
          Traffic Data Monitor
        </h1>
        <button
          onClick={handleLogout}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          {getTranslation(language, "logout")}
        </button>
        <select
          onChange={(e) => setLanguage(e.target.value)}
          value={language}
          className="ml-4 bg-gray-200 border border-gray-400 rounded-md py-1 px-2"
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-xl font-semibold animate-spin"
        />
        <div className="text-xl font-semibold ml-2">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {renderHeader()}

      <div className="w-full p-2 md:p-4 bg-gray-100">
        <div className="w-full bg-white rounded-lg shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-600" />
              Filter Traffic Data
            </h2>

            <div className="flex items-center gap-3">
              {dateRange[0].startDate && dateRange[0].endDate && (
                <div className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                  {formatDate(dateRange[0].startDate)} -{" "}
                  {formatDate(dateRange[0].endDate)}
                  <button
                    onClick={() => clearFilterType("dateRange")}
                    className="ml-2 text-blue-500 hover:text-blue-700"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              <ExportCSVButton
                data={transformDataForCSV(filteredData)}
                filename="traffics.csv"
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded flex items-center"
              />

              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded flex items-center"
              >
                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                {showDatePicker ? "Hide Date Range" : "Select Date Range"}
              </button>
            </div>
          </div>

          {showDatePicker && (
            <div className="relative">
              <div className="absolute right-0 z-50 mt-1">
                <div className="border border-gray-300 rounded-lg shadow-md bg-white">
                  <DateRangePicker
                    ranges={dateRange}
                    onChange={(ranges) => setDateRange([ranges.selection])}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                Customer
              </label>
              <Select
                key={`customer-select-${selectKey}`}
                options={customerOptions}
                isMulti
                onChange={setSelectedCustomers}
                value={selectedCustomers}
                placeholder="Select Customers"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                Division
              </label>
              <Select
                key={`division-select-${selectKey}`}
                options={divisionOptions}
                isMulti
                onChange={setSelectedDivisions}
                value={selectedDivisions}
                placeholder="Select Divisions"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faIdCard}
                  className="mr-1 text-blue-600"
                />
                RFC
              </label>
              <Select
                key={`rfc-select-${selectKey}`}
                options={rfcOptions}
                isMulti
                onChange={setSelectedRfcs}
                value={selectedRfcs}
                placeholder="Select RFCs"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faCog} className="mr-1 text-blue-600" />
                Operation Type
              </label>
              <Select
                key={`op-type-select-${selectKey}`}
                options={operationTypeOptions}
                isMulti
                onChange={setSelectedOperationTypes}
                value={selectedOperationTypes}
                placeholder="Select Operation Types"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-1 text-blue-600"
                />
                Entry Point
              </label>
              <Select
                key={`entry-point-select-${selectKey}`}
                options={entryPointOptions}
                isMulti
                onChange={setSelectedEntryPoints}
                value={selectedEntryPoints}
                placeholder="Select Entry Points"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-1 text-blue-600"
                />
                Pedimento Number
              </label>
              <Select
                key={`pedimento-select-${selectKey}`}
                options={pedimentoOptions}
                isMulti
                onChange={setSelectedPedimentos}
                value={selectedPedimentos}
                placeholder="Select Pedimento Numbers"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faClipboardCheck}
                  className="mr-1 text-blue-600"
                />
                Status
              </label>
              <Select
                key={`status-select-${selectKey}`}
                options={statusOptions}
                isMulti
                onChange={setSelectedStatus}
                value={selectedStatus}
                placeholder="Select Status"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
          </div>

          <div className="mt-5">
            <SearchForm
              onSearch={handleSearch}
              placeholder="Search in all fields..."
              className="max-w-lg mx-auto"
              searchTerm={searchTerm} // Pass the current search term
              clearSearch={() => clearFilterType("search")} // Pass the clear function
            />
          </div>

          {(selectedCustomers.length > 0 ||
            selectedRfcs.length > 0 ||
            selectedOperationTypes.length > 0 ||
            selectedEntryPoints.length > 0 ||
            selectedPedimentos.length > 0 ||
            selectedStatus.length > 0 ||
            searchTerm) && (
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="mr-2">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {selectedCustomers.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Customer: {item.label}
                      <button
                        onClick={() =>
                          setSelectedCustomers((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}
                  {selectedDivisions.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Division: {item.label}
                      <button
                        onClick={() =>
                          setSelectedDivisions((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}
                  {selectedRfcs.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      RFC: {item.label}
                      <button
                        onClick={() =>
                          setSelectedRfcs((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {selectedOperationTypes.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Op. Type: {item.label}
                      <button
                        onClick={() =>
                          setSelectedOperationTypes((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {selectedEntryPoints.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Entry: {item.label}
                      <button
                        onClick={() =>
                          setSelectedEntryPoints((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {selectedPedimentos.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Pedimento: {item.label}
                      <button
                        onClick={() =>
                          setSelectedPedimentos((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {selectedStatus.map((item) => (
                    <span
                      key={item.value}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                    >
                      Status: {item.label}
                      <button
                        onClick={() =>
                          setSelectedStatus((prev) =>
                            prev.filter((i) => i.value !== item.value)
                          )
                        }
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  ))}

                  {searchTerm && (
                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center">
                      Search: "{searchTerm}"
                      <button
                        onClick={() => clearFilterType("search")}
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </span>
                  )}

                  <button
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-800 underline text-xs ml-2"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <MiniDashboardTraffic trafficData={filteredData} />
        <Pagination
          total={sortedData.length}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        <div className="bg-white p-1 md:p-2 shadow-md rounded-lg overflow-x-auto w-full">
          <table className="min-w-full table-fixed border-collapse">
            <thead className="bg-gray-100 border-b-2 border-gray-500">
              <tr>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("reference")}
                >
                  <div className="flex items-center justify-center">
                    <span>Reference</span>
                    {getSortIcon("reference") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("reference")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("pedimento.pedimento_entry_point")}
                >
                  <div className="flex items-center justify-center">
                    <span>Entry Point</span>
                    {getSortIcon("pedimento.pedimento_entry_point") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("pedimento.pedimento_entry_point")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("pedimento.pedimento_number")}
                >
                  <div className="flex items-center justify-center">
                    <span>Pedimento Number</span>
                    {getSortIcon("pedimento.pedimento_number") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("pedimento.pedimento_number")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("trailer_number")}
                >
                  <div className="flex items-center justify-center">
                    <span>Trailer Number</span>
                    {getSortIcon("trailer_number") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("trailer_number")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("purchase_order")}
                >
                  <div className="flex items-center justify-center">
                    <span>Purchase Order</span>
                    {getSortIcon("purchase_order") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("purchase_order")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("total_packages")}
                >
                  <div className="flex items-center justify-center">
                    <span>Total Packages</span>
                    {getSortIcon("total_packages") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("total_packages")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("weight")}
                >
                  <div className="flex items-center justify-center">
                    <span>Weight</span>
                    {getSortIcon("weight") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("weight")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("delivery_type")}
                >
                  <div className="flex items-center justify-center">
                    <span>Delivery Type</span>
                    {getSortIcon("delivery_type") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("delivery_type")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("operation_type")}
                >
                  <div className="flex items-center justify-center">
                    <span>Operation Type</span>
                    {getSortIcon("operation_type") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("operation_type")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("customer_name")}
                >
                  <div className="flex items-center justify-center">
                    <span>Customer Name</span>
                    {getSortIcon("customer_name") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("customer_name")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("customer_id")}
                >
                  <div className="flex items-center justify-center">
                    <span>Customer ID</span>
                    {getSortIcon("customer_id") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("customer_id")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("customer_rfc")}
                >
                  <div className="flex items-center justify-center">
                    <span>Customer RFC</span>
                    {getSortIcon("customer_rfc") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("customer_rfc")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("detail.dates_data.date_init")}
                >
                  <div className="flex items-center justify-center">
                    <span>Created</span>
                    {getSortIcon("detail.dates_data.date_init") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("detail.dates_data.date_init")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("detail.traffic_data.status")}
                >
                  <div className="flex items-center justify-center">
                    <span>Status</span>
                    {getSortIcon("detail.traffic_data.status") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("detail.traffic_data.status")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("attention_time")}
                >
                  <div className="flex items-center justify-center">
                    <span>Attention Time</span>
                    {getSortIcon("attention_time") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("attention_time")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 cursor-pointer"
                  onClick={() => requestSort("doc_time")}
                >
                  <div className="flex items-center justify-center">
                    <span>Doc. Time</span>
                    {getSortIcon("doc_time") && (
                      <FontAwesomeIcon
                        icon={getSortIcon("doc_time")}
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th className="w-1/16 py-3 px-2 uppercase font-semibold text-xs text-gray-700 text-center">
                  <div className="flex items-center justify-center">
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const isRowExpanded = expandedRows.includes(
                  item.header.reference
                );
                return (
                  <React.Fragment key={`${item.header.reference}-${index}`}>
                    <tr
                      className={`border-b border-gray-200 hover:bg-gray-50 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.reference}
                      >
                        {item.header.reference}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.pedimento.pedimento_entry_point}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <FontAwesomeIcon
                            icon={
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).icon
                            }
                            className={`${
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).color
                            } mr-1`}
                            title={
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).title
                            }
                          />
                          <span>
                            {item.header.pedimento.pedimento_entry_point}
                          </span>
                        </div>
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.pedimento.pedimento_number}
                      >
                        {item.header.pedimento.pedimento_number}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.trailer_number}
                      >
                        {item.header.trailer_number}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.purchase_order}
                      >
                        {item.header.purchase_order}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.total_packages}
                      >
                        {item.header.total_packages}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.weight}
                      >
                        {item.header.weight}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.delivery_type}
                      >
                        {item.header.delivery_type}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={getOperationTypeText(item.header.operation_type)}
                      >
                        {getOperationTypeText(item.header.operation_type)}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.customer_name}
                      >
                        {item.header.customer_name}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.customer_id}
                      >
                        {item.header.customer_id}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={item.header.customer_rfc}
                      >
                        {item.header.customer_rfc}
                      </td>
                      <td
                        className="w-1/16 py-2 px-2 text-xs text-center truncate"
                        title={formatDate(
                          item.header.detail?.dates_data?.date_init
                        )}
                      >
                        {formatDate(item.header.detail?.dates_data?.date_init)}
                      </td>
                      <td className="w-1/16 py-2 px-2 text-xs text-center">
                        <span
                          className={
                            item.header.detail?.traffic_data?.status ===
                            "Pagado"
                              ? "text-green-500 font-semibold"
                              : "text-gray-500 font-semibold"
                          }
                        >
                          {item.header.detail?.traffic_data?.status === "Pagado"
                            ? "Paid"
                            : "Open"}
                        </span>
                      </td>
                      <td className="w-1/16 py-2 px-2 text-xs text-center">
                        {calculateAttentionTime(item.header.detail?.dates_data)}
                      </td>
                      <td className="w-1/16 py-2 px-2 text-xs text-center">
                        {calculateDocTime(item.header.detail?.dates_data)}
                      </td>
                      <td className="w-1/16 py-2 px-2 text-xs text-center">
                        <div className="flex justify-center items-center">
                          <button
                            onClick={() => handleDetailsClick(item)}
                            className="text-blue-500 hover:text-blue-700 mx-1"
                            title="View Details (opens in new tab)"
                          >
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </button>
                          <button
                            onClick={() =>
                              handleRowClick(item.header.reference)
                            }
                            className="text-blue-500 hover:text-blue-700 mx-1"
                            title={
                              isRowExpanded ? "Collapse Row" : "Expand Row"
                            }
                          >
                            <FontAwesomeIcon
                              icon={isRowExpanded ? faChevronUp : faChevronDown}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isRowExpanded && (
                      <tr>
                        <td colSpan="17" className="bg-gray-100 p-4">
                          <TrafficDetailSection currentItemDetails={item} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={!!modalData}
        onRequestClose={closeModal}
        contentLabel="Details Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <div className="p-6 bg-white rounded-lg shadow-lg max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Details</h2>
            <button
              onClick={closeModal}
              className="text-red-500 hover:text-red-700"
            >
              Close
            </button>
          </div>
          {modalData && <TrafficDetailSection currentItemDetails={modalData} />}
        </div>
      </Modal>
    </>
  );
};

export default TrafficComponent;
